/**
 * The glyphs `/konto` draws and nothing else in this app does: the camera on the avatar, the two
 * clipboard sheets, the arrow leaving for a published page, the notice's mark, the three theme
 * marks, and the door. The tick and the eye are NOT here — `shared/ui/AuthIcons` already draws
 * both, and a second drawing of one glyph is two things to change when the set moves.
 *
 * Inline SVG on `currentColor`, the convention `shared/ui/AuthIcons` and `app/ui/NavIcons` both
 * take — one drawing that works in both themes without a second asset. Every one is `aria-hidden`:
 * each sits beside a labelled control or a row that already says what it means.
 *
 * They live in the slice rather than in `shared/ui` because this is their FIRST consumer, which is
 * the repo's rule. The day a second surface needs the external-link arrow it moves, not before.
 */

interface IconProps {
  readonly className?: string;
}

const STROKE_PROPS = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
} as const;

/** The mockup's `camera` — the small disc hanging off the avatar's corner. */
export function CameraIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE_PROPS}>
      <path d="M3.5 8.5h3.2l1.5-2.5h7.6l1.5 2.5h3.2v11h-17z" />
      <circle cx="12" cy="13.5" r="3.4" />
    </svg>
  );
}

/** The mockup's `info` — the store-renewal notice's mark, inside the erase dialog. */
export function InfoIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE_PROPS}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5.5" />
      <path d="M12 7.6v.1" />
    </svg>
  );
}

/** The mockup's `ext` — a link that leaves this tab. */
export function ExternalIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE_PROPS}>
      <path d="M14 4.5h5.5V10" />
      <path d="M19.5 4.5L11 13" />
      <path d="M18 14.5v3a3 3 0 0 1-3 3H6.5a3 3 0 0 1-3-3V9a3 3 0 0 1 3-3h3" />
    </svg>
  );
}

/** The mockup's `mail` — the glyph frame D sets beside `contact@movebuddy.pl`. */
export function MailIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE_PROPS}>
      <rect x="3" y="5" width="18" height="14" rx="3" />
      <path d="M3.5 8l8.5 6 8.5-6" />
    </svg>
  );
}

export function SunIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE_PROPS}>
      <circle cx="12" cy="12" r="4.2" />
      <path d="M12 2.5v2.2M12 19.3v2.2M4.2 4.2l1.6 1.6M18.2 18.2l1.6 1.6M2.5 12h2.2M19.3 12h2.2M4.2 19.8l1.6-1.6M18.2 5.8l1.6-1.6" />
    </svg>
  );
}

export function MoonIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE_PROPS}>
      <path d="M20 13.5A8 8 0 0 1 10.5 4a8.2 8.2 0 1 0 9.5 9.5z" />
    </svg>
  );
}

/** „Systemowy" — a display, because the choice is the machine's rather than the trainer's. */
export function MonitorIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE_PROPS}>
      <rect x="2.8" y="4.2" width="18.4" height="12.6" rx="2.5" />
      <path d="M9 20.3h6M12 16.8v3.5" />
    </svg>
  );
}

/** The header's „Wyloguj się": a door with an arrow leaving it. */
export function SignOutIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE_PROPS}>
      <path d="M14.5 4.5h3a2.5 2.5 0 0 1 2.5 2.5v10a2.5 2.5 0 0 1-2.5 2.5h-3" />
      <path d="M10.5 8.5L14.5 12l-4 3.5" />
      <path d="M14.5 12H4" />
    </svg>
  );
}
